import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';

import { CHILD_DEFAULT_SORT, CHILD_FILTER_THRESHOLD, CHILD_SORT_KEY } from '../constants';
import {
  buildPackaging,
  catalogName,
  comparePreferences,
  dashIfEmpty,
  formatTimestamp,
  preferenceMatches,
} from '../utils/formatters';

const HEAD_CELL_SX = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  py: 1,
};

const BODY_CELL_SX = { py: 1.25, verticalAlign: 'top' };

const COLUMNS = [
  { id: 'productId', label: 'Product ID', width: 150 },
  { id: 'catalogName', label: 'Catalog Name', sortKey: CHILD_SORT_KEY.CATALOG_NAME },
  { id: 'inventoryDrugName', label: 'Inventory Name' },
  { id: 'ndc', label: 'Preferred NDC', width: 170 },
  { id: 'manufacturer', label: 'Manufacturer', sortKey: CHILD_SORT_KEY.MANUFACTURER, width: 150 },
  { id: 'updated', label: 'Updated', sortKey: CHILD_SORT_KEY.UPDATED, width: 130 },
];

const PartnerPreferenceDrugTable = ({
  partner,
  preferences,
  loading,
  error,
  onRetry,
  canManage,
  onAddForPartner,
  onChange,
  onRemove,
}) => {
  // View state, deliberately local: the row unmounts on collapse, so expanding a
  // different partner starts clean rather than carrying a stale filter over.
  const [sort, setSort] = useState(CHILD_DEFAULT_SORT);
  const [filterTerm, setFilterTerm] = useState('');

  // Below the threshold a filter box is chrome, not help — the common case is a
  // handful of preferences, and it should stay uncluttered.
  const showFilter = preferences.length > CHILD_FILTER_THRESHOLD;

  const visible = useMemo(() => {
    const filtered = showFilter
      ? preferences.filter((preference) => preferenceMatches(preference, filterTerm))
      : preferences;
    return [...filtered].sort(comparePreferences(sort.key, sort.order));
  }, [preferences, filterTerm, showFilter, sort]);

  const handleSort = (sortKey) => {
    setSort((current) =>
      current.key === sortKey
        ? { key: sortKey, order: current.order === 'asc' ? 'desc' : 'asc' }
        : { key: sortKey, order: 'asc' }
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  // Children are fetched per expanded row, so a failed read is scoped to this row
  // and gets its own retry — the outer table's error state cannot serve it.
  if (error) {
    return (
      <Alert
        severity='error'
        action={
          <Button color='inherit' size='small' onClick={onRetry}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  const columnCount = canManage ? COLUMNS.length + 1 : COLUMNS.length;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
        <Typography
          variant='caption'
          sx={{
            fontWeight: 700,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          {/* Always the total, never the filtered count: a shrinking number here
              would read as preferences having disappeared. */}
          {`Preferences (${preferences.length})`}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {showFilter && (
          <TextField
            size='small'
            value={filterTerm}
            onChange={(event) => setFilterTerm(event.target.value)}
            placeholder='Filter drug / NDC / manufacturer'
            InputProps={{
              inputProps: { 'aria-label': `Filter preferences for ${partner.partnerName}` },
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 280, bgcolor: 'background.paper' }}
          />
        )}
      </Box>

      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflowX: 'auto',
        }}
      >
        <Table size='small'>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{ ...HEAD_CELL_SX, width: column.width }}
                  sortDirection={column.sortKey === sort.key ? sort.order : false}
                >
                  {column.sortKey ? (
                    <TableSortLabel
                      active={column.sortKey === sort.key}
                      direction={column.sortKey === sort.key ? sort.order : 'asc'}
                      onClick={() => handleSort(column.sortKey)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              {canManage && <TableCell sx={{ ...HEAD_CELL_SX, width: 170 }} align='right' />}
            </TableRow>
          </TableHead>
          <TableBody>
            {preferences.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnCount} sx={{ borderBottom: 'none' }}>
                  <Typography variant='body2' sx={{ color: 'text.secondary', py: 1 }}>
                    No active preferences for this partner.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {preferences.length > 0 && visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnCount} sx={{ borderBottom: 'none' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                      No preference matches
                    </Typography>
                    <Button size='small' onClick={() => setFilterTerm('')}>
                      Clear
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {visible.map((preference) => {
              const packaging = buildPackaging(preference);
              return (
                <TableRow key={`${preference.productId}-${preference.ndc}`} hover>
                  <TableCell sx={{ ...BODY_CELL_SX, fontFamily: 'monospace', fontSize: 13 }}>
                    {preference.productId}
                  </TableCell>

                  <TableCell sx={BODY_CELL_SX}>
                    <Typography variant='body2' sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                      {catalogName(preference)}
                    </Typography>
                    {packaging && (
                      <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                        {packaging}
                      </Typography>
                    )}
                  </TableCell>

                  {/* The inventory master's own name for the preferred NDC. It can
                      differ from the catalog name — that divergence is exactly what
                      someone checking a preference needs to see. */}
                  <TableCell sx={BODY_CELL_SX}>
                    <Typography variant='body2'>
                      {dashIfEmpty(preference.inventoryDrugName)}
                    </Typography>
                  </TableCell>

                  <TableCell sx={BODY_CELL_SX}>
                    <Box
                      component='span'
                      sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13 }}
                    >
                      {preference.ndc}
                    </Box>
                  </TableCell>

                  <TableCell sx={BODY_CELL_SX}>
                    <Typography variant='body2'>{dashIfEmpty(preference.manufacturer)}</Typography>
                  </TableCell>

                  <TableCell sx={BODY_CELL_SX}>
                    <Typography variant='body2'>
                      {formatTimestamp(preference.modifiedTimestamp)}
                    </Typography>
                  </TableCell>

                  {canManage && (
                    <TableCell sx={BODY_CELL_SX} align='right'>
                      <Button size='small' onClick={() => onChange(preference)}>
                        Change
                      </Button>
                      {/* Lower visual weight than Change on purpose: repointing an
                          NDC is the routine action, clearing a preference is rare
                          and hands the drug back to default allocation. */}
                      <Button size='small' color='inherit' onClick={() => onRemove(preference)}>
                        Remove
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}

            {canManage && (
              <TableRow>
                <TableCell
                  colSpan={columnCount}
                  align='center'
                  sx={{ borderBottom: 'none', bgcolor: 'action.hover' }}
                >
                  <Button size='small' onClick={onAddForPartner}>
                    {`+ Add drug preference for ${partner.partnerName}`}
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </>
  );
};

PartnerPreferenceDrugTable.propTypes = {
  partner: PropTypes.shape({
    partnerId: PropTypes.string.isRequired,
    partnerName: PropTypes.string.isRequired,
  }).isRequired,
  preferences: PropTypes.arrayOf(PropTypes.shape({ productId: PropTypes.string })),
  loading: PropTypes.bool,
  error: PropTypes.string,
  onRetry: PropTypes.func.isRequired,
  canManage: PropTypes.bool.isRequired,
  onAddForPartner: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

PartnerPreferenceDrugTable.defaultProps = {
  preferences: [],
  loading: false,
  error: '',
};

export default PartnerPreferenceDrugTable;
